<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    header="Поиск недостающих данных товаров"
    :style="{ width: '55rem' }"
    :closable="!isProcessing"
    :closeOnEscape="!isProcessing"
  >
    <div v-if="!isProcessing" class="p-fluid">
      <!-- Шаг 1: Выбор брендов -->
      <div class="field mb-4">
        <label class="block text-900 font-medium mb-2">
          <i class="pi pi-filter mr-2"></i>
          Бренды для обогащения
        </label>
        <small class="text-600 block mb-2">
          Выберите бренды, товары которых нужно обогатить. Если ничего не выбрано - будут обработаны все бренды.
        </small>
        <MultiSelect
          v-model="selectedBrands"
          :options="brandOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Все бренды"
          display="chip"
          :filter="true"
          class="w-full"
        />
      </div>

      <!-- Шаг 2: Выбор полей -->
      <div class="field mb-4">
        <label class="block text-900 font-medium mb-2">
          <i class="pi pi-list mr-2"></i>
          Поля для поиска
        </label>
        <small class="text-600 block mb-2">
          Выберите какие характеристики нужно найти. Система будет искать данные только для пустых полей.
        </small>

        <!-- Быстрый выбор -->
        <div class="mb-3 flex gap-2">
          <Button
            label="Выбрать все"
            icon="pi pi-check-square"
            class="p-button-sm p-button-text"
            @click="selectAllFields"
          />
          <Button
            label="Снять все"
            icon="pi pi-stop"
            class="p-button-sm p-button-text"
            @click="selectedFields = []"
          />
        </div>

        <div class="grid">
          <div
            v-for="field in availableFields"
            :key="field.value"
            class="col-12 md:col-6"
          >
            <div class="flex align-items-center mb-2">
              <Checkbox
                v-model="selectedFields"
                :inputId="field.value"
                :value="field.value"
              />
              <label :for="field.value" class="ml-2 cursor-pointer">
                <strong>{{ field.label }}</strong>
                <br />
                <small class="text-600">{{ field.description }}</small>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Прогресс обогащения -->
    <div v-else class="enrichment-progress">
      <div class="text-center mb-3">
        <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
        <h3 class="mt-3">Обогащение данных товаров</h3>
        <p class="text-600">
          Идет поиск информации через Perplexity AI...
        </p>
      </div>

      <!-- Прогресс бар -->
      <ProgressBar
        :value="progressPercent"
        :showValue="true"
        class="mb-3"
      />

      <div class="flex justify-content-between mb-4">
        <span>Обработано товаров: {{ progress.current }} / {{ progress.total }}</span>
        <span>{{ progressPercent }}%</span>
      </div>

      <!-- Текущий товар -->
      <div v-if="currentProduct" class="current-product-card p-3 mb-3 surface-50 border-round">
        <div class="flex align-items-center mb-2">
          <Tag
            :value="productStatusLabel"
            :severity="productStatusSeverity"
            class="mr-2"
          />
          <span class="font-bold">{{ currentProduct.product_brand }} {{ currentProduct.product_part_number }}</span>
        </div>
        <div v-if="currentProduct.product_name" class="text-sm text-600 mb-2">
          {{ currentProduct.product_name }}
        </div>
        <div v-if="currentProduct.updated_fields && currentProduct.updated_fields.length > 0" class="mt-2">
          <small class="text-600">Обновлено полей:</small>
          <div class="flex flex-wrap gap-1 mt-1">
            <Chip
              v-for="field in currentProduct.updated_fields"
              :key="field"
              :label="getFieldLabel(field)"
              class="text-xs"
            />
          </div>
        </div>
        <div v-if="currentProduct.error" class="mt-2 text-red-500 text-sm">
          <i class="pi pi-exclamation-triangle mr-1"></i>
          {{ currentProduct.error }}
        </div>
      </div>

      <!-- Статистика -->
      <div class="stats-grid grid">
        <div class="col-3 text-center">
          <div class="text-2xl font-bold text-green-500">{{ stats.updated }}</div>
          <div class="text-xs text-600">Обновлено</div>
        </div>
        <div class="col-3 text-center">
          <div class="text-2xl font-bold text-yellow-500">{{ stats.skipped }}</div>
          <div class="text-xs text-600">Пропущено</div>
        </div>
        <div class="col-3 text-center">
          <div class="text-2xl font-bold text-red-500">{{ stats.failed }}</div>
          <div class="text-xs text-600">Ошибок</div>
        </div>
        <div class="col-3 text-center">
          <div class="text-2xl font-bold text-blue-500">{{ stats.processed }}</div>
          <div class="text-xs text-600">Всего</div>
        </div>
      </div>
    </div>

    <!-- Результаты после завершения -->
    <div v-if="isCompleted" class="completion-summary">
      <div class="text-center mb-4">
        <i class="pi pi-check-circle text-6xl text-green-500 mb-3"></i>
        <h3 class="text-2xl mb-2">Обогащение завершено!</h3>
        <p class="text-600">
          Обработано {{ finalStats.processed }} товаров из {{ finalStats.total_products }}
        </p>
      </div>

      <div class="grid mb-4">
        <div class="col-3 text-center p-3 border-round surface-100">
          <div class="text-3xl font-bold text-green-600 mb-1">{{ finalStats.updated }}</div>
          <div class="text-sm text-600">Обновлено</div>
        </div>
        <div class="col-3 text-center p-3 border-round surface-100">
          <div class="text-3xl font-bold text-yellow-600 mb-1">{{ finalStats.skipped }}</div>
          <div class="text-sm text-600">Пропущено</div>
        </div>
        <div class="col-3 text-center p-3 border-round surface-100">
          <div class="text-3xl font-bold text-red-600 mb-1">{{ finalStats.failed }}</div>
          <div class="text-sm text-600">Ошибок</div>
        </div>
        <div class="col-3 text-center p-3 border-round surface-100">
          <div class="text-3xl font-bold text-blue-600 mb-1">{{ finalStats.total_products }}</div>
          <div class="text-sm text-600">Всего</div>
        </div>
      </div>

      <!-- Детальная статистика по полям -->
      <div v-if="Object.keys(finalStats.fields_filled || {}).length > 0" class="field">
        <label class="block text-900 font-medium mb-2">Заполнено полей:</label>
        <div class="grid">
          <div
            v-for="(count, field) in finalStats.fields_filled"
            :key="field"
            class="col-6 mb-2"
          >
            <div class="flex justify-content-between align-items-center p-2 surface-50 border-round">
              <span class="font-medium">{{ getFieldLabel(field) }}</span>
              <Badge :value="count" severity="success" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <Button
        v-if="!isProcessing && !isCompleted"
        label="Отмена"
        icon="pi pi-times"
        class="p-button-text"
        @click="closeDialog"
      />
      <Button
        v-if="!isProcessing && !isCompleted"
        label="Начать поиск"
        icon="pi pi-search"
        class="p-button-primary"
        @click="startEnrichment"
        :disabled="selectedFields.length === 0"
      />
      <Button
        v-if="isCompleted"
        label="Закрыть"
        icon="pi pi-check"
        class="p-button-success"
        @click="closeDialog"
      />
    </template>
  </Dialog>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import Dialog from 'primevue/dialog'
import MultiSelect from 'primevue/multiselect'
import Checkbox from 'primevue/checkbox'
import Button from 'primevue/button'
import ProgressBar from 'primevue/progressbar'
import Tag from 'primevue/tag'
import Chip from 'primevue/chip'
import Badge from 'primevue/badge'
import apiService from '../services/apiService'

export default {
  name: 'ProductEnrichmentDialog',
  components: {
    Dialog,
    MultiSelect,
    Checkbox,
    Button,
    ProgressBar,
    Tag,
    Chip,
    Badge
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:visible', 'enrichment-completed'],
  setup(props, { emit }) {
    const toast = useToast()

    // Локальное состояние видимости
    const isVisible = computed({
      get: () => props.visible,
      set: (value) => emit('update:visible', value)
    })

    // Данные
    const brandOptions = ref([])
    const selectedBrands = ref([])
    const availableFields = ref([])
    const selectedFields = ref([])

    // Состояние процесса
    const isProcessing = ref(false)
    const isCompleted = ref(false)
    const progress = ref({ current: 0, total: 0 })
    const currentProduct = ref(null)
    const stats = ref({
      updated: 0,
      skipped: 0,
      failed: 0,
      processed: 0
    })
    const finalStats = ref({})

    // WebSocket
    let ws = null

    // Вычисляемые свойства
    const progressPercent = computed(() => {
      if (progress.value.total === 0) return 0
      return Math.round((progress.value.current / progress.value.total) * 100)
    })

    const productStatusLabel = computed(() => {
      if (!currentProduct.value) return ''
      const status = currentProduct.value.status
      if (status === 'success') return 'Успешно'
      if (status === 'error') return 'Ошибка'
      if (status === 'skipped') return 'Пропущено'
      if (status === 'no_data') return 'Нет данных'
      return 'Обработка...'
    })

    const productStatusSeverity = computed(() => {
      if (!currentProduct.value) return 'info'
      const status = currentProduct.value.status
      if (status === 'success') return 'success'
      if (status === 'error') return 'danger'
      if (status === 'skipped') return 'warning'
      if (status === 'no_data') return 'info'
      return 'info'
    })

    // Методы
    const loadBrandOptions = async () => {
      try {
        const response = await apiService.getFilterOptions()
        const data = response.data
        brandOptions.value = (data.brands || []).map(b => ({
          label: b,
          value: b
        }))
      } catch (error) {
        console.error('Ошибка загрузки брендов:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось загрузить список брендов',
          life: 3000
        })
      }
    }

    const loadAvailableFields = async () => {
      try {
        const response = await apiService.getEnrichableFields()
        availableFields.value = response.fields
      } catch (error) {
        console.error('Ошибка загрузки полей:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось загрузить список полей',
          life: 3000
        })
      }
    }

    const selectAllFields = () => {
      selectedFields.value = availableFields.value.map(f => f.value)
    }

    const getFieldLabel = (fieldValue) => {
      const field = availableFields.value.find(f => f.value === fieldValue)
      return field ? field.label : fieldValue
    }

    const connectWebSocket = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const wsUrl = `${protocol}//${window.location.host}/api/enrich/ws-progress`

      ws = new WebSocket(wsUrl)

      ws.onopen = () => {
      }

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          handleWebSocketMessage(message)
        } catch (error) {
          console.error('Ошибка парсинга WebSocket сообщения:', error)
        }
      }

      ws.onerror = (error) => {
        console.error('Ошибка WebSocket:', error)
      }

      ws.onclose = () => {
      }
    }

    const handleWebSocketMessage = (message) => {
      if (message.type === 'progress') {
        progress.value.current = message.current
        progress.value.total = message.total
        currentProduct.value = message

        // Обновляем статистику
        if (message.status === 'success') {
          stats.value.updated++
        } else if (message.status === 'error') {
          stats.value.failed++
        } else if (message.status === 'skipped') {
          stats.value.skipped++
        }
        stats.value.processed = message.current
      } else if (message.type === 'completed') {
        finalStats.value = message.stats
        isProcessing.value = false
        isCompleted.value = true

        toast.add({
          severity: 'success',
          summary: 'Обогащение завершено',
          detail: `Обновлено товаров: ${message.stats.updated}`,
          life: 5000
        })

        emit('enrichment-completed', message.stats)
      } else if (message.type === 'error') {
        isProcessing.value = false
        toast.add({
          severity: 'error',
          summary: 'Ошибка обогащения',
          detail: message.error,
          life: 5000
        })
      }
    }

    const startEnrichment = async () => {
      if (selectedFields.value.length === 0) {
        toast.add({
          severity: 'warn',
          summary: 'Предупреждение',
          detail: 'Выберите хотя бы одно поле для поиска',
          life: 3000
        })
        return
      }

      // Подключаем WebSocket перед началом
      connectWebSocket()

      // Сбрасываем состояние
      isProcessing.value = true
      isCompleted.value = false
      progress.value = { current: 0, total: 0 }
      currentProduct.value = null
      stats.value = {
        updated: 0,
        skipped: 0,
        failed: 0,
        processed: 0
      }

      try {
        // Запускаем обогащение
        await apiService.enrichProducts(
          selectedBrands.value.length > 0 ? selectedBrands.value : null,
          selectedFields.value
        )
        // Результат придет через WebSocket
      } catch (error) {
        console.error('Ошибка запуска обогащения:', error)
        isProcessing.value = false

        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: error.response?.data?.detail || 'Не удалось запустить обогащение',
          life: 5000
        })

        if (ws) {
          ws.close()
          ws = null
        }
      }
    }

    const closeDialog = () => {
      if (ws) {
        ws.close()
        ws = null
      }

      isVisible.value = false

      // Сбрасываем состояние при закрытии
      setTimeout(() => {
        isProcessing.value = false
        isCompleted.value = false
        selectedBrands.value = []
        selectedFields.value = []
        progress.value = { current: 0, total: 0 }
        currentProduct.value = null
        stats.value = {
          updated: 0,
          skipped: 0,
          failed: 0,
          processed: 0
        }
        finalStats.value = {}
      }, 300)
    }

    // Lifecycle hooks
    onMounted(() => {
      loadBrandOptions()
      loadAvailableFields()
    })

    onUnmounted(() => {
      if (ws) {
        ws.close()
        ws = null
      }
    })

    // Watch для закрытия WebSocket при закрытии диалога
    watch(() => props.visible, (newValue) => {
      if (!newValue && ws) {
        ws.close()
        ws = null
      }
    })

    return {
      isVisible,
      brandOptions,
      selectedBrands,
      availableFields,
      selectedFields,
      isProcessing,
      isCompleted,
      progress,
      progressPercent,
      currentProduct,
      productStatusLabel,
      productStatusSeverity,
      stats,
      finalStats,
      selectAllFields,
      getFieldLabel,
      startEnrichment,
      closeDialog
    }
  }
}
</script>

<style scoped>
.enrichment-progress {
  min-height: 300px;
}

.current-product-card {
  border-left: 4px solid var(--primary-color);
}

.stats-grid {
  margin-top: 1rem;
  padding: 1rem;
  background: var(--surface-50);
  border-radius: 8px;
}

.completion-summary {
  padding: 1rem 0;
}
</style>
